import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, GraduationCap, BookOpen, Cpu, Compass, Sun, Users, Building2, Handshake, Check, ChevronDown } from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { useT } from "@/i18n/helpers";
import Seo from "@/components/Seo";

const Section = ({ children, className = "", id }) => (
 <section id={id} className={`border-t border-nanda-line ${className}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">{children}</div>
 </section>
);
const Overline = ({ children }) => (<div className="label-overline text-nanda-muted mb-4">{children}</div>);

export default function AcademiePremium() {
 const tt = useT();

 const FAMILLES = [
 { i: Sun, t: tt("Énergie éducative", "Educational energy", "Energia educativa"),
 d: tt("Sensibilisation à l'énergie, découverte du solaire, ateliers pratiques dans les écoles et les villages, culture des solutions durables.",
 "Energy awareness, discovery of solar, practical workshops in schools and villages, culture of sustainable solutions.",
 "Sensibilização para a energia, descoberta do solar, ateliers práticos nas escolas e aldeias, cultura das soluções sustentáveis.") },
 { i: Cpu, t: tt("Numérique", "Digital", "Digital"),
 d: tt("Culture numérique, bureautique, intelligence artificielle, préparation aux outils du monde du travail. Initiation dès le plus jeune âge.",
 "Digital literacy, office skills, artificial intelligence, preparation for workplace tools. Early-age introduction.",
 "Cultura digital, escritório, inteligência artificial, preparação para as ferramentas do mundo do trabalho. Iniciação desde a mais tenra idade.") },
 { i: BookOpen, t: tt("Lecture & culture", "Reading & culture", "Leitura e cultura"),
 d: tt("Accès aux ressources éducatives, ateliers de lecture et d'écriture, patrimoine ouest-africain, rencontres avec des auteurs.",
 "Access to educational resources, reading and writing workshops, West African heritage, meetings with authors.",
 "Acesso a recursos educativos, ateliers de leitura e escrita, património oeste-africano, encontros com autores.") },
 { i: Compass, t: tt("Orientation & avenir", "Guidance & future", "Orientação e futuro"),
 d: tt("Orientation scolaire et professionnelle, découverte des métiers, préparation du CV, de la lettre et des entretiens, immersions en entreprise.",
 "School and career guidance, profession discovery, CV and cover letter preparation, interview coaching, business immersions.",
 "Orientação escolar e profissional, descoberta de profissões, preparação do CV, da carta e das entrevistas, imersões em empresa.") },
 ];

 const AXES = [
 tt("Orientation scolaire et professionnelle", "School and career guidance", "Orientação escolar e profissional"),
 tt("Découverte des métiers", "Career discovery", "Descoberta de profissões"),
 tt("Culture numérique", "Digital literacy", "Cultura digital"),
 tt("Sensibilisation à l'énergie et aux solutions durables", "Awareness of energy and sustainable solutions", "Sensibilização para a energia e para as soluções sustentáveis"),
 tt("Entrepreneuriat et structuration de projets", "Entrepreneurship and project structuring", "Empreendedorismo e estruturação de projectos"),
 tt("Compétences personnelles et professionnelles", "Personal and professional skills", "Competências pessoais e profissionais"),
 tt("Leadership féminin", "Female leadership", "Liderança feminina"),
 tt("Préparation du CV et de la lettre de motivation", "CV and cover letter preparation", "Preparação do CV e da carta de motivação"),
 tt("Préparation aux entretiens", "Interview preparation", "Preparação para entrevistas"),
 tt("Rencontres et immersions professionnelles", "Meetings and business immersions", "Encontros e imersões profissionais"),
 tt("Lecture, culture et accès aux ressources éducatives", "Reading, culture and access to educational resources", "Leitura, cultura e acesso aos recursos educativos"),
 tt("Programmes co-construits avec les partenaires", "Programmes co-built with partners", "Programas co-construídos com os parceiros"),
 ];

 const PUBLICS = [
 { i: Users, t: tt("Jeunes & élèves", "Youth & students", "Jovens e alunos"),
 d: tt("Du collège au lycée, jusqu'à l'entrée dans le monde professionnel.",
 "From middle school to high school, up to entering the professional world.",
 "Do 3.º ciclo ao secundário, até à entrada no mundo profissional.") },
 { i: Building2, t: tt("Établissements scolaires", "Educational institutions", "Estabelecimentos escolares"),
 d: tt("Collèges, lycées, centres de formation, universités.",
 "Middle schools, high schools, training centres, universities.",
 "Colégios, liceus, centros de formação, universidades.") },
 { i: Handshake, t: tt("Entreprises & associations", "Businesses & associations", "Empresas e associações"),
 d: tt("Interventions, immersions, mentorat, mécénat de compétences.",
 "Interventions, immersions, mentoring, skills-based sponsorship.",
 "Intervenções, imersões, mentoria, mecenato de competências.") },
 { i: GraduationCap, t: tt("Porteurs de projets", "Project leaders", "Promotores de projectos"),
 d: tt("Entrepreneurs, artisans, femmes leaders, porteurs d'initiatives locales.",
 "Entrepreneurs, craftspeople, women leaders, local initiative leaders.",
 "Empreendedores, artesãos, mulheres líderes, promotores de iniciativas locais.") },
 ];

 const METHODE = [
 { n: "01", t: tt("Écoute des besoins", "Listening to needs", "Escuta das necessidades"),
 d: tt("Rencontre avec l'établissement, l'entreprise, l'association ou la collectivité.",
 "Meeting with the school, the business, the association or the local authority.",
 "Encontro com o estabelecimento, a empresa, a associação ou a autarquia.") },
 { n: "02", t: tt("Co-construction du programme", "Programme co-building", "Co-construção do programa"),
 d: tt("Adaptation des contenus, du format et du calendrier au contexte local.",
 "Adaptation of contents, format and schedule to the local context.",
 "Adaptação dos conteúdos, do formato e do calendário ao contexto local.") },
 { n: "03", t: tt("Intervention & animation", "Intervention & facilitation", "Intervenção e animação"),
 d: tt("Ateliers, conférences, immersions, rencontres, restitutions.",
 "Workshops, conferences, immersions, meetings, presentations.",
 "Ateliers, conferências, imersões, encontros, restituições.") },
 { n: "04", t: tt("Suivi & évaluation", "Follow-up & evaluation", "Acompanhamento e avaliação"),
 d: tt("Bilan avec les bénéficiaires, mesure d'impact, ajustements pour la suite.",
 "Review with beneficiaries, impact measurement, adjustments for the next steps.",
 "Balanço com os beneficiários, medida de impacto, ajustes para o seguimento.") },
 ];

 const PARTENAIRES = [
 tt("Écoles, collèges, lycées, centres de formation", "Schools, colleges, high schools, training centres", "Escolas, colégios, liceus, centros de formação"),
 tt("Collectivités locales et administrations", "Local authorities and administrations", "Autarquias locais e administrações"),
 tt("Associations et fondations", "Associations and foundations", "Associações e fundações"),
 tt("Entreprises et employeurs locaux", "Local businesses and employers", "Empresas e empregadores locais"),
 tt("Partenaires institutionnels", "Institutional partners", "Parceiros institucionais"),
 tt("Diaspora et mécènes", "Diaspora and patrons", "Diáspora e mecenas"),
 ];

 return (
 <div data-testid="page-academie-premium">
 <Seo />
 <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden bg-nanda-ink">
 <img src="/photos/academie-hero.jpg" alt={tt("NANDA SÁ Académie, orientation et éducation en Guinée-Bissau", "NANDA SÁ Académie, guidance and education in Guinea-Bissau", "NANDA SÁ Académie, orientação e educação na Guiné-Bissau")} className="absolute inset-0 h-full w-full object-cover opacity-70" loading="eager" />
 <div className="absolute inset-0 bg-gradient-to-b from-nanda-ink/40 via-nanda-ink/25 to-nanda-ink/80" />
 <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-24 md:pb-32">
 <FadeUp>
 <div className="label-overline text-white/85 mb-6">NANDA SÁ Académie</div>
 <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-5xl">
 {tt("Éduquer. Orienter.", "Educate. Guide.", "Educar. Orientar.")}<br />
 <span className="text-nanda-gold italic">{tt("Révéler les talents.", "Reveal talents.", "Revelar talentos.")}</span>
 </h1>
 <p className="mt-8 max-w-2xl text-white/85 text-lg md:text-xl leading-relaxed">
 {tt("Une initiative dédiée à l'éducation, à l'orientation, au développement des compétences et à l'accompagnement des jeunes, des établissements, des entrepreneurs et des porteurs de projets en Guinée-Bissau.",
 "An initiative dedicated to education, guidance, skills development and support for young people, schools, entrepreneurs and project leaders in Guinea-Bissau.",
 "Uma iniciativa dedicada à educação, à orientação, ao desenvolvimento das competências e ao acompanhamento dos jovens, dos estabelecimentos, dos empreendedores e dos promotores de projectos na Guiné-Bissau.")}
 </p>
 <div className="mt-12 flex flex-wrap gap-4">
 <Link to="/contact?subject=Academie-programme" className="inline-flex items-center gap-3 bg-white text-nanda-ink px-8 py-4 label-overline hover:bg-nanda-gold hover:text-white transition-colors duration-500" data-testid="academie-cta-programme">
 {tt("Construire un programme avec nous", "Co-build a programme with us", "Construir um programa connosco")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <a href="#familles" className="inline-flex items-center gap-3 border border-white/50 text-white px-8 py-4 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="academie-cta-familles">
 {tt("Voir les 4 familles", "See the 4 families", "Ver as 4 famílias")} <ChevronDown size={16} strokeWidth={1.5} />
 </a>
 </div>
 </FadeUp>
 </div>
 </section>

 <Section>
 <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
 <FadeUp className="md:col-span-5">
 <Overline>{tt("Notre mission", "Our mission", "A nossa missão")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Un espace d'orientation, d'apprentissage et de rencontres.", "A space for guidance, learning and encounters.", "Um espaço de orientação, aprendizagem e encontros.")}
 </h2>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 text-nanda-inkSoft text-[17px] leading-relaxed space-y-5">
 <p>{tt("NANDA SÁ Académie n'est pas un centre de formation technique unique. C'est une initiative éducative plus large qui agit sur l'orientation, la culture numérique, la lecture, la découverte des métiers, l'entrepreneuriat et le leadership.",
 "NANDA SÁ Académie is not a single technical training centre. It is a broader educational initiative acting on guidance, digital literacy, reading, career discovery, entrepreneurship and leadership.",
 "A NANDA SÁ Académie não é um centro único de formação técnica. É uma iniciativa educativa mais ampla que age sobre a orientação, a cultura digital, a leitura, a descoberta de profissões, o empreendedorismo e a liderança.")}</p>
 <p>{tt("Notre conviction : l'avenir se construit ici, avec ceux qui y vivent. Chaque programme est co-construit avec les écoles, les collectivités, les associations, les entreprises et les partenaires institutionnels.",
 "Our conviction: the future is built here, with those who live here. Every programme is co-built with schools, local authorities, associations, businesses and institutional partners.",
 "A nossa convicção: o futuro constrói-se aqui, com aqueles que aqui vivem. Cada programa é co-construído com as escolas, as autarquias, as associações, as empresas e os parceiros institucionais.")}</p>
 <p>{tt("Nous travaillons en Guinée-Bissau, en français, en portugais et en anglais.",
 "We work in Guinea-Bissau, in French, Portuguese and English.",
 "Trabalhamos na Guiné-Bissau, em francês, português e inglês.")}</p>
 </FadeUp>
 </div>
 </Section>

 <Section className="bg-nanda-bgAlt" id="familles">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Nos quatre familles de programmes", "Our four programme families", "As nossas quatro famílias de programas")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Quatre portes d'entrée, une même exigence.", "Four entry points, one same standard.", "Quatro portas de entrada, uma mesma exigência.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-px bg-nanda-line border border-nanda-line">
 {FAMILLES.map((f) => (
 <StaggerItem key={f.t} className="bg-white p-10 md:p-14">
 <f.i size={32} strokeWidth={1.2} className="text-nanda-terracotta mb-5" />
 <div className="font-serif text-3xl text-nanda-ink leading-tight">{f.t}</div>
 <p className="mt-4 text-[15px] text-nanda-inkSoft leading-relaxed">{f.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 <Section>
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Nos principaux axes", "Our main axes", "Os nossos principais eixos")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Douze axes de travail concrets.", "Twelve concrete lines of work.", "Doze eixos de trabalho concretos.")}
 </h2>
 </FadeUp>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
 {AXES.map((a) => (
 <div key={a} className="flex items-start gap-3 py-3 border-t border-nanda-line">
 <Check size={16} strokeWidth={2} className="text-nanda-terracotta mt-1 shrink-0" />
 <span className="text-[15px] text-nanda-ink leading-snug">{a}</span>
 </div>
 ))}
 </div>
 </Section>

 <Section className="bg-nanda-bgAlt">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Nos publics", "Our audiences", "Os nossos públicos")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Quatre publics, un même objectif.", "Four audiences, one same objective.", "Quatro públicos, um mesmo objectivo.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {PUBLICS.map((p) => (
 <StaggerItem key={p.t} className="bg-white p-8 md:p-10">
 <p.i size={26} strokeWidth={1.2} className="text-nanda-ink mb-4" />
 <div className="font-serif text-xl text-nanda-ink leading-tight">{p.t}</div>
 <p className="mt-3 text-sm text-nanda-inkSoft leading-relaxed">{p.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 <Section>
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Notre méthode", "Our method", "O nosso método")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Quatre étapes co-construites.", "Four co-built steps.", "Quatro etapas co-construídas.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {METHODE.map((m) => (
 <StaggerItem key={m.n} className="bg-nanda-bgAlt p-8 md:p-10">
 <div className="font-serif text-nanda-terracotta text-4xl md:text-5xl mb-4">{m.n}</div>
 <div className="font-serif text-xl text-nanda-ink leading-tight">{m.t}</div>
 <p className="mt-3 text-sm text-nanda-inkSoft leading-relaxed">{m.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 <Section className="bg-nanda-bgAlt">
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("Avec qui nous travaillons", "Who we work with", "Com quem trabalhamos")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Six familles de partenaires.", "Six families of partners.", "Seis famílias de parceiros.")}
 </h2>
 </FadeUp>
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {PARTENAIRES.map((p) => (<div key={p} className="bg-white p-6 md:p-8"><div className="font-serif text-lg md:text-xl text-nanda-ink">{p}</div></div>))}
 </div>
 </Section>

 <Section className="bg-nanda-ink text-white">
 <FadeUp className="max-w-4xl mx-auto text-center">
 <Overline><span className="text-nanda-gold">{tt("Nous accompagner", "Support us", "Acompanhe-nos")}</span></Overline>
 <h3 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.08] mt-4">
 {tt("Construisons ensemble", "Let us build together", "Construamos juntos")}<br />
 <span className="text-nanda-gold italic">{tt("l'avenir des jeunes d'ici.", "the future of the youth here.", "o futuro dos jovens daqui.")}</span>
 </h3>
 <p className="mt-8 text-white/80 text-lg leading-relaxed max-w-3xl mx-auto">
 {tt("Écoles, entreprises, collectivités, associations, mécènes : présentons-nous, échangeons, imaginons ensemble un programme qui a du sens pour votre territoire.",
 "Schools, businesses, local authorities, associations, patrons: let us introduce ourselves, exchange and imagine together a programme that makes sense for your territory.",
 "Escolas, empresas, autarquias, associações, mecenas: apresentemo-nos, troquemos, imaginemos juntos um programa com sentido para o vosso território.")}
 </p>
 <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl mx-auto">
 <Link to="/contact?subject=Academie-programme" className="inline-flex items-center justify-center gap-2 bg-nanda-gold text-white px-6 py-4 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="academie-cta-1">
 {tt("Construire un programme", "Co-build a programme", "Construir um programa")} <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 <Link to="/contact?subject=Academie-partenaire" className="inline-flex items-center justify-center gap-2 border border-white/50 text-white px-6 py-4 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="academie-cta-2">
 {tt("Devenir partenaire", "Become a partner", "Tornar-se parceiro")} <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 <Link to="/contact?subject=Academie-intervention" className="inline-flex items-center justify-center gap-2 border border-white/50 text-white px-6 py-4 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="academie-cta-3">
 {tt("Accueillir une intervention", "Host an intervention", "Acolher uma intervenção")} <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 <Link to="/contact?subject=Academie-projet" className="inline-flex items-center justify-center gap-2 border border-white/50 text-white px-6 py-4 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="academie-cta-4">
 {tt("Nous présenter un projet", "Present a project to us", "Apresentar-nos um projecto")} <ArrowUpRight size={14} strokeWidth={1.5} />
 </Link>
 </div>
 </FadeUp>
 </Section>
 </div>
 );
}
